<template>
  <div v-if="data">
    <component :is="menuTop"/>

    <v-main>
      <div class="gr-silo">

        <!-- Migas de pan -->
        <v-container>
          <nav class="gr-migas" :aria-label="lang === 'ca' ? 'Ruta de navegació' : 'Ruta de navegación'">
            <ol>
              <li v-for="(m, i) in data.migas" :key="'m' + i">
                <router-link v-if="i < data.migas.length - 1" :to="m.url">{{ m.titulo }}</router-link>
                <span v-else aria-current="page">{{ m.titulo }}</span>
                <span v-if="i < data.migas.length - 1" class="gr-miga-sep">›</span>
              </li>
            </ol>
          </nav>
        </v-container>

        <!-- Cabecera -->
        <header class="gr-cabecera">
          <v-container>
            <p class="gr-eyebrow">
              <v-icon small color="#900A30">{{ data.icono }}</v-icon>
              {{ data.categoria.categoria[lang].h1 }}
            </p>
            <h1 class="gr-h1">{{ data.h1 }}</h1>
            <p v-if="data.entradilla" class="gr-entradilla">{{ data.entradilla }}</p>
          </v-container>
        </header>

        <!-- Cuerpo -->
        <v-container>
          <section v-for="(sec, i) in data.secciones" :key="'s' + i" class="gr-seccion">
            <h2 class="gr-h2">{{ sec.h2 }}</h2>

            <p v-for="(p, j) in (sec.parrafos || [])" :key="'p' + j" class="gr-body">{{ p }}</p>

            <ul v-if="sec.lista && sec.lista.length" class="gr-lista">
              <li v-for="(li, j) in sec.lista" :key="'l' + j">{{ li }}</li>
            </ul>

            <p v-if="sec.destacado" class="gr-destacado">{{ sec.destacado }}</p>
          </section>
        </v-container>

        <!-- Preguntas frecuentes -->
        <v-container v-if="data.faqs.length">
          <section class="gr-faqs">
            <h2 class="gr-h2">{{ lang === 'ca' ? 'Preguntes freqüents' : 'Preguntas frecuentes' }}</h2>
            <div v-for="(f, i) in data.faqs" :key="'f' + i" class="gr-faq">
              <h3 class="gr-faq-q">{{ f.q }}</h3>
              <p class="gr-faq-a">{{ f.a }}</p>
            </div>
          </section>
        </v-container>

        <!-- Llamada a la acción -->
        <v-container>
          <section class="gr-cta-bloque">
            <h2 class="gr-cta-titulo">{{ data.ctaTitulo }}</h2>
            <p class="gr-cta-texto">{{ data.ctaTexto }}</p>
            <v-btn
              :href="enlaceWhatsapp"
              target="_blank"
              rel="noopener"
              x-large
              depressed
              class="gr-cta-btn"
            >
              <v-icon left>mdi-whatsapp</v-icon>
              {{ lang === 'ca' ? 'Escriu-nos per WhatsApp' : 'Escríbenos por WhatsApp' }}
            </v-btn>
            <!-- El número de seguros es solo de WhatsApp: allí no se ofrece llamar -->
            <a v-if="!data.soloWhatsapp" :href="'tel:' + telefono" class="gr-cta-tel">
              <v-icon small color="#fff">mdi-phone</v-icon>
              {{ telefonoTexto }}
            </a>
          </section>
        </v-container>

        <!-- Enlazado interno hacia hermanos del mismo silo -->
        <v-container v-if="data.relacionados.length">
          <section class="gr-grupo">
            <h2 class="gr-h2">
              {{ lang === 'ca' ? 'Potser també t\'interessa' : 'Quizá también te interese' }}
            </h2>
            <v-row>
              <v-col v-for="(r, i) in data.relacionados" :key="'r' + i" cols="12" sm="6" md="4">
                <router-link :to="r.url" class="gr-tarjeta">
                  <v-icon class="gr-tarjeta-icono">{{ r.icono }}</v-icon>
                  <span class="gr-tarjeta-titulo">{{ r.titulo }}</span>
                  <p class="gr-tarjeta-texto">{{ r.resumen }}</p>
                  <span class="gr-tarjeta-mas">
                    {{ lang === 'ca' ? 'Més informació' : 'Más información' }} ›
                  </span>
                </router-link>
              </v-col>
            </v-row>
          </section>
        </v-container>

        <!-- Vuelta a la categoría: cierra el silo -->
        <v-container>
          <section class="gr-otros-silos">
            <h2 class="gr-otros-titulo">
              {{ lang === 'ca' ? 'Torna a' : 'Vuelve a' }}
            </h2>
            <router-link :to="urlCategoria" class="gr-otro-silo">
              <v-icon small>{{ data.categoria.icono }}</v-icon>
              {{ data.categoria.categoria[lang].h1 }}
            </router-link>
            <router-link
              v-for="silo in otrosSilos"
              :key="silo.id"
              :to="silo.url"
              class="gr-otro-silo"
            >
              <v-icon small>{{ silo.icono }}</v-icon>
              {{ silo.titulo }}
            </router-link>
          </section>
        </v-container>

      </div>
    </v-main>

    <component :is="menuBottom"/>
    <component :is="footer"/>
  </div>
</template>

<script>
  import seo from '@/mixins/seo';
  import contenido from '@/content';

  import MenuTopEsp from '@/components/MenuTopEsp';
  import MenuTopCat from '@/components/MenuTopCat';
  import MenuBottomEsp from '@/components/MenuBottomEsp';
  import MenuBottomCat from '@/components/MenuBottomCat';
  import FooterEsp from '@/components/FooterEsp';
  import FooterCat from '@/components/FooterCat';

  export default {
    name: 'PageServicio',

    mixins: [seo],

    props: {
      categoriaId: { type: String, required: true },
      slug: { type: String, required: true },
      lang: { type: String, default: 'es' }
    },

    computed: {
      data() {
        return contenido.datosServicio(this.categoriaId, this.slug, this.lang);
      },

      seoData() {
        return this.data;
      },

      telefono() {
        return (this.data && this.data.telefono) || contenido.SITE.telefono;
      },

      telefonoTexto() {
        return (this.data && this.data.telefonoTexto) || contenido.SITE.telefonoTexto;
      },

      enlaceWhatsapp() {
        return 'https://wa.me/' + this.data.whatsapp +
          '?text=' + encodeURIComponent(this.data.ctaWhatsapp);
      },

      urlCategoria() {
        return contenido.urlCategoria(this.data.categoria, this.lang);
      },

      otrosSilos() {
        var actual = this.categoriaId;
        return contenido.navegacionSilos(this.lang).filter(function (s) {
          return s.id !== actual;
        });
      },

      menuTop() { return this.lang === 'ca' ? MenuTopCat : MenuTopEsp; },
      menuBottom() { return this.lang === 'ca' ? MenuBottomCat : MenuBottomEsp; },
      footer() { return this.lang === 'ca' ? FooterCat : FooterEsp; }
    },

    /* Si alguien llega a un slug que no existe o que no está publicado,
       lo mandamos a la página de categoría en lugar de dejar la página vacía */
    created() {
      if (!this.data) {
        var cat = contenido.getCategoria(this.categoriaId);
        this.$router.replace(contenido.urlCategoria(cat, this.lang));
      }
    }
  };
</script>

<style src="@/assets/scss/silo.scss" lang="scss"></style>
